import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { SessionService } from "./session.service";

/**
 * Programa dos señales basadas en el campo `expiracion` de la sesión:
 *  - `sessionExpiring$`: pre-aviso (60s antes de expirar).
 *  - `sessionExpired$`: instante en que la sesión caduca.
 *
 * Cualquier cambio en `SessionService.sesion$` (login, refresh, logout)
 * reprograma los timeouts automáticamente.
 */
@Injectable({ providedIn: "root" })
export class SessionTimerService implements OnDestroy {
    sessionExpiring$ = new Subject<void>();
    sessionExpired$ = new Subject<void>();

    private readonly preavisoMs = 60 * 1000;

    private expiringTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private expiredTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private sesionSub: Subscription | null = null;

    constructor(
        private sessionService: SessionService,
        private ngZone: NgZone
    ) {}

    watchSession(): void {
        this.sesionSub?.unsubscribe();
        this.sesionSub = this.sessionService.sesion$.subscribe((sesion) => {
            this.stop();
            if (sesion?.expiracion) {
                this.programar(new Date(sesion.expiracion).getTime());
            }
        });
    }

    stop(): void {
        if (this.expiringTimeoutId !== null) {
            clearTimeout(this.expiringTimeoutId);
            this.expiringTimeoutId = null;
        }
        if (this.expiredTimeoutId !== null) {
            clearTimeout(this.expiredTimeoutId);
            this.expiredTimeoutId = null;
        }
    }

    ngOnDestroy(): void {
        this.stop();
        this.sesionSub?.unsubscribe();
    }

    private programar(expiracionMs: number): void {
        const ahora = Date.now();
        const msHastaExpirar = expiracionMs - ahora;

        if (msHastaExpirar <= 0) {
            this.ngZone.run(() => this.sessionExpired$.next());
            return;
        }

        const msHastaPreaviso = msHastaExpirar - this.preavisoMs;

        this.ngZone.runOutsideAngular(() => {
            if (msHastaPreaviso > 0) {
                this.expiringTimeoutId = setTimeout(() => {
                    this.ngZone.run(() => this.sessionExpiring$.next());
                }, msHastaPreaviso);
            } else {
                this.ngZone.run(() => this.sessionExpiring$.next());
            }

            this.expiredTimeoutId = setTimeout(() => {
                this.ngZone.run(() => this.sessionExpired$.next());
            }, msHastaExpirar);
        });
    }
}
